// Смена пароля из кабинета: кнопка, которая разворачивается в форму.
//
// Отдельный компонент, потому что мест два: попап пользователя и бургер-меню
// на странице поездок. Дублировать форму нельзя — у неё своя обработка ошибок
// сервера ('weak_password' с перечнем претензий) и предупреждение о том, что
// остальные входы закроются.
import { useId, useState, type FormEvent, type ReactElement } from 'react';
import { useT } from '../i18n';
import { changePassword } from './api';
import { errorKey, passwordProblemKeys } from './errors';
import type { ApiError } from './api';
import styles from './PasswordChangeForm.module.css';

export interface PasswordChangeFormProps {
  /** Пароль сменён — экран может закрыть за собой меню или попап. */
  onDone?(): void;
}

export function PasswordChangeForm({ onDone }: PasswordChangeFormProps = {}): ReactElement {
  const t = useT();
  const ids = useId();

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await changePassword({ current, next });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setDone(true);
    setCurrent('');
    setNext('');
    setOpen(false);
    onDone?.();
  }

  const problems = passwordProblemKeys(error?.problems);

  if (!open) {
    return (
      <div className={styles.wrap}>
        {done && <p className={styles.notice}>{t('auth.password.changed')}</p>}
        <button type="button" className={styles.btn} onClick={() => setOpen(true)}>
          {t('auth.password.change')}
        </button>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {error && (
        <p className={styles.error} role="alert">
          {t(errorKey(error))}
          {problems.length > 0 && (
            <ul className={styles.errorList}>
              {problems.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ul>
          )}
        </p>
      )}

      <label className={styles.label} htmlFor={`${ids}-current`}>
        {t('auth.password.current')}
      </label>
      <input
        id={`${ids}-current`}
        className={styles.control}
        type="password"
        autoComplete="current-password"
        value={current}
        required
        onChange={(e) => setCurrent(e.target.value)}
      />

      <label className={styles.label} htmlFor={`${ids}-next`}>
        {t('auth.password.new')}
      </label>
      <input
        id={`${ids}-next`}
        className={styles.control}
        type="password"
        autoComplete="new-password"
        value={next}
        required
        onChange={(e) => setNext(e.target.value)}
      />
      <p className={styles.hint}>{t('auth.pw.rule')}</p>
      <p className={styles.hint}>{t('account.password.warning')}</p>

      <div className={styles.buttonRow}>
        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>
          {t('auth.password.change')}
        </button>
        <button
          type="button"
          className={styles.btn}
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          {t('form.cancel')}
        </button>
      </div>
    </form>
  );
}
